import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useApi from '../../hooks/useApi';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../common';
import './ManageSharedAccessDialog.css';

/**
 * List people a dashboard or folder is shared with; remove access (DELETE /api/sharing/:id).
 */
export default function ManageSharedAccessDialog({
  open,
  onClose,
  resourceType,
  resourceId,
  resourceLabel,
  tenantId,
  canManage,
  /** Called after a share is removed successfully so the parent can refresh counts/lists. */
  onSharesChanged,
}) {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError('');
    }
  }, [error, showErrorToast]);

  const load = useCallback(async () => {
    if (!open || !resourceId || !tenantId) return;
    setLoading(true);
    setError('');
    try {
      const res =
        resourceType === 'folder'
          ? await api.get('/sharing', {
              params: { folderId: resourceId },
              headers: { 'X-Tenant-ID': tenantId },
            })
          : await api.get(`/sharing/${resourceId}`, {
              headers: { 'X-Tenant-ID': tenantId },
            });
      const list = res.data?.data?.shares ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not load shares.'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api, open, resourceId, tenantId, resourceType]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRemove = async (shareRow) => {
    const sid =
      shareRow.DashboardShareId ||
      shareRow.dashboardShareId ||
      shareRow.FolderShareId ||
      shareRow.folderShareId;
    if (!sid) return;
    setRemovingId(String(sid));
    setError('');
    try {
      await api.delete(`/sharing/${sid}`, {
        params: { type: resourceType === 'dashboard' ? 'dashboard' : 'folder' },
        headers: { 'X-Tenant-ID': tenantId },
      });
      await load();
      onSharesChanged?.();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Remove failed.'));
    } finally {
      setRemovingId(null);
    }
  };

  const title = `Shared with — ${resourceLabel || (resourceType === 'dashboard' ? 'Dashboard' : 'Folder')}`;

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      fullWidth
      maxWidth="sm"
      className="manage-shared-dialog"
      aria-labelledby="manage-shared-title"
    >
      <DialogTitle
        id="manage-shared-title"
        sx={{
          fontFamily: FONT_FAMILY_PRIMARY,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
        }}
      >
        <Typography component="span" variant="h6" sx={{ fontFamily: FONT_FAMILY_PRIMARY, pr: 1 }}>
          {title}
        </Typography>
        <IconButton aria-label="Close" onClick={() => onClose(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!canManage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You do not have permission to change sharing for this item.
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
            No users have been given access yet.
          </Typography>
        ) : (
          <Table size="small" className="manage-shared-dialog__table">
            <TableHead>
              <TableRow>
                <TableCell>User email</TableCell>
                <TableCell>Access type</TableCell>
                {canManage && <TableCell align="right"> </TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const email = r.SharedWith || r.sharedWith || r.Email;
                const perm = r.Permission || r.permission || r.PermissionLevel || r.permissionLevel;
                const sid =
                  r.DashboardShareId ||
                  r.dashboardShareId ||
                  r.FolderShareId ||
                  r.folderShareId;
                return (
                  <TableRow key={String(sid)}>
                    <TableCell sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>{email}</TableCell>
                    <TableCell sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>{perm}</TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label={`Remove ${email}`}
                          disabled={removingId === String(sid)}
                          onClick={() => handleRemove(r)}
                        >
                          {removingId === String(sid) ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteOutlineIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
